import {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { X, ChevronRight, ChevronLeft, GraduationCap, Zap, ListChecks, PenLine } from "lucide-react";
import { Link, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useTutorial } from "@/hooks/use-tutorial";
import { useAuth } from "@/hooks/use-auth";

const MAIN_SEEN_KEY = (userId: string) => `tpt_tour_seen_${userId}`;
const PAGE_KEY = (id: string, userId: string) => `tpt_page_${id}_${userId}`;

interface TourStep {
  id: string;
  title: string;
  content: React.ReactNode;
  targetTestId: string | null;
  placement: "right" | "left" | "bottom" | "top" | "center";
  highlightPadding?: number;
  isChoiceStep?: boolean;
}

const MAIN_TOUR_STEPS: TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to The Proper Tool!",
    content:
      "Let\u2019s take a quick tour of the key features so you can hit the ground running. You can skip at any time and restart the tour from the graduation cap icon in the top bar.",
    targetTestId: null,
    placement: "center",
  },
  {
    id: "quote-options",
    title: "3 ways to build a quote",
    content: (
      <div className="space-y-2">
        <p>Three quoting methods sit at the top of the sidebar:</p>
        <ul className="space-y-1.5 text-sm">
          <li>
            <span className="font-semibold text-foreground">Auto Quote:</span>{" "}
            Upload drawings, photos, or PDF plans (or describe the job in plain English) and the smart quoting engine builds a full itemised quote automatically.
          </li>
          <li>
            <span className="font-semibold text-foreground">Template Quote:</span>{" "}
            Choose a pre-built structure for common job types such as loft conversions or extensions, then adjust the figures.
          </li>
          <li>
            <span className="font-semibold text-foreground">Manual Quote:</span>{" "}
            Add line items one by one for complete control over every figure.
          </li>
        </ul>
      </div>
    ),
    targetTestId: "group-quote-options",
    placement: "right",
    highlightPadding: 6,
  },
];

const PAGE_TOURS: Record<string, TourStep[]> = {
  dashboard: [
    {
      id: "dashboard-intro",
      title: "Your Dashboard",
      content:
        "The Dashboard shows your quoting activity at a glance: total quotes, total revenue, and how much time and money The Proper Tool has saved you compared to quoting manually. Your most recent quotes appear at the bottom for quick access.",
      targetTestId: null,
      placement: "center",
    },
  ],
  settings: [
    {
      id: "settings-intro",
      title: "Settings",
      content:
        "Add your company name, logo, brand colour, and contact details. These appear on every PDF you send to clients. You can also set your default labour rate, markup, overhead, payment terms, and manage your subscription plan.",
      targetTestId: null,
      placement: "center",
    },
  ],
  "auto-quote": [
    {
      id: "auto-quote-1",
      title: "Add Files or Describe the Job",
      content:
        "Upload photos, drawings, or PDF plans for best results. The tool reads these to build a more accurate quote. If you don't have files to hand, describe the job in plain English. Use the project type tags to help the tool categorise the work correctly.",
      targetTestId: null,
      placement: "center",
    },
    {
      id: "auto-quote-2",
      title: "Generate Your Quote",
      content:
        "The smart quoting engine builds a full itemised quote based on your files and description. You can then review, edit, add, or remove any line item before saving or exporting.",
      targetTestId: "button-analyze",
      placement: "top",
    },
  ],
  "auto-quote-review": [
    {
      id: "review-1",
      title: "Estimate Summary and Confidence",
      content:
        "The confidence badge tells you how complete the input was. High confidence means all key details were provided. Medium or low means some gaps were found and assumptions have been made. Review the project summary at the top before editing any individual figures.",
      targetTestId: "badge-confidence",
      placement: "bottom",
    },
    {
      id: "review-2",
      title: "Review Findings",
      content:
        "This section flags potential issues spotted during analysis, such as scope gaps, quantity concerns, price outliers, or unit mismatches. Each flag explains what to check and why. Work through these before finalising your prices to avoid surprises later.",
      targetTestId: "card-validation-flags",
      placement: "top",
    },
    {
      id: "review-3",
      title: "Suggested Additional Items",
      content:
        "These are items likely needed for the project but not explicitly mentioned in your description. Review each one carefully. Click Accept to add any that apply to your quote, or Dismiss to skip items that are not relevant. Adding overlooked items now avoids costly variations later.",
      targetTestId: "card-suggested-items",
      placement: "top",
    },
    {
      id: "review-4",
      title: "Editing Prices and Quantities",
      content:
        "Every line item is editable. Click directly on a quantity, unit, or unit cost field to change the value. The subtotal updates in real time. You can also edit the item name and description, reorder items using the arrows, remove items you don't need, or add new ones at the bottom.",
      targetTestId: "input-item-cost-0",
      placement: "top",
    },
    {
      id: "review-5",
      title: "Learn Price",
      content:
        "Once you are happy with a unit cost, click Learn price next to that item. The tool stores this rate against the trade category and uses it to calibrate all future quotes automatically. The more prices you learn, the less manual editing your future quotes will need.",
      targetTestId: "button-learn-price-0",
      placement: "top",
    },
    {
      id: "review-6",
      title: "Learn Adjustment",
      content:
        "Use Learn Adjustment to apply a percentage uplift or reduction across an entire trade category in one step. For example, if your groundworks rates consistently run 15% above the default, set that once here and every future quote will apply it automatically.",
      targetTestId: "button-learn-adjustment",
      placement: "bottom",
    },
    {
      id: "review-7",
      title: "Price Scale Slider",
      content:
        "The price scale slider scales every line item up or down by up to 50%, adjusting the overall quote total in one move. Use it to explore different price points quickly before fine-tuning individual items. Slide left to reduce and right to increase.",
      targetTestId: "slider-subtotal",
      placement: "top",
    },
  ],
  "template-quote": [
    {
      id: "template-quote-1",
      title: "Template Quote",
      content:
        "Select a template for the type of job you\u2019re pricing, such as a loft conversion, house extension, bathroom, or kitchen fit-out. The template pre-fills a structured set of line items with typical quantities and units. Adjust each figure to match your specific job, then save or export the finished quote.",
      targetTestId: null,
      placement: "center",
    },
  ],
  "manual-quote": [
    {
      id: "manual-quote-1",
      title: "Manual Quote",
      content:
        "Add line items one by one. Each item has a description, quantity, unit, and unit rate. Use this when you already know exactly what\u2019s in scope and want full control over every figure. The finished quote can be exported as a branded PDF and shared with your client.",
      targetTestId: null,
      placement: "center",
    },
  ],
  quotes: [
    {
      id: "quotes-1",
      title: "Your Quotes",
      content:
        "Every quote you\u2019ve created appears here. Click any quote to open, edit, or view the full breakdown. Use the status tabs to find Draft, Sent, Accepted, or Confirmed quotes quickly.",
      targetTestId: "text-quotes-title",
      placement: "bottom",
    },
    {
      id: "quotes-2",
      title: "Search, share, and export",
      content:
        "Use the search bar to find quotes by address or description. Open any quote to share it with your client for digital sign-off, or export it as a professionally branded PDF.",
      targetTestId: "input-search-quotes",
      placement: "bottom",
    },
  ],
  clients: [
    {
      id: "clients-1",
      title: "Client Management",
      content:
        "Save your client contacts here: name, email, phone, and address. Once added, attach a client to any quote and their details will appear automatically on the quote document and every PDF you export.",
      targetTestId: "button-new-client",
      placement: "bottom",
    },
  ],
  "cost-library": [
    {
      id: "cost-library-1",
      title: "Browse by trade",
      content:
        "The Cost Library covers 30+ trade categories, from groundworks and structural to decorating and fit-out. Click a category in the left column to see its line items and current rates.",
      targetTestId: "sidebar-cost-nav",
      placement: "right",
    },
    {
      id: "cost-library-2",
      title: "Set your own rates",
      content:
        "Click any item to override its rate with what you actually charge. Your overridden rates appear highlighted and are applied automatically in every future Auto Quote. You can also add custom items for specialist work not in the default list.",
      targetTestId: "button-add-custom-task",
      placement: "bottom",
    },
  ],
  adjustments: [
    {
      id: "adjustments-1",
      title: "Learned Adjustments",
      content:
        "Every time you change a price in a quote, that adjustment is recorded here. The tool uses these records to calibrate future Auto Quotes to your real-world pricing. The more you use and edit quotes, the more accurate future quotes become. You can also add manual adjustments to fine-tune specific trades or categories.",
      targetTestId: null,
      placement: "center",
    },
  ],
  "pricing-intelligence": [
    {
      id: "pricing-intelligence-1",
      title: "Pricing Accuracy Index",
      content:
        "This gauge shows how closely the tool's output matches your historical rates. The score rises as you import more past quotes. The higher it gets, the less manual editing your future Auto Quotes will need.",
      targetTestId: "card-pai-dashboard",
      placement: "bottom",
    },
    {
      id: "pricing-intelligence-2",
      title: "Category Intelligence",
      content:
        "Once you have imported quotes, each trade category shows its signal count and match strength. Categories with fewer signals are where adding more import data will have the biggest impact on accuracy.",
      targetTestId: "card-category-intelligence",
      placement: "bottom",
    },
    {
      id: "pricing-intelligence-3",
      title: "Imports, Price Signals and Rules",
      content:
        "The three tabs below let you manage everything. Imports is where you upload past quotes. Price Signals shows the rates extracted from them. Rules lets you apply percentage uplifts or discounts across specific trades.",
      targetTestId: "tabs-pricing-intelligence",
      placement: "bottom",
    },
    {
      id: "pricing-intelligence-4",
      title: "Import Historical Quotes",
      content:
        "Upload your past quotes or invoices here as PDFs or spreadsheets. The tool extracts pricing data from each file and turns it into price signals. The more you import, the more accurate your learned pricing becomes.",
      targetTestId: "dropzone-quote-files",
      placement: "top",
    },
  ],
  "site-surveys": [
    {
      id: "site-surveys-1",
      title: "Your Site Surveys",
      content:
        "Every site visit you\u2019ve captured appears here. Open one to keep editing your notes, download the PDF report, or send it back into Auto Quote to refresh the quote from the latest measurements.",
      targetTestId: "text-site-surveys-title",
      placement: "bottom",
    },
    {
      id: "site-surveys-2",
      title: "Start a New Site Survey",
      content:
        "Press New Site Survey to begin a visit. You\u2019ll capture client and project details, walk through each room with photos and voice notes, and finish with a single tap to generate the report.",
      targetTestId: "button-new-site-survey",
      placement: "bottom",
    },
    {
      id: "site-surveys-3",
      title: "Search and Filter",
      content:
        "Find a past visit fast by client name, address or project type. Use the status filter to narrow down to drafts you haven\u2019t finished yet or completed surveys ready for Auto Quote.",
      targetTestId: "input-search-site-surveys",
      placement: "bottom",
    },
  ],
  "quote-survey": [
    {
      id: "quote-survey-1",
      title: "Site Survey",
      content:
        "Capture everything you need for an accurate quote in one place: client, project type, photos, voice notes and room-by-room scope. Nothing here is sent to your client \u2014 it\u2019s your private working file.",
      targetTestId: null,
      placement: "center",
    },
    {
      id: "quote-survey-2",
      title: "Project Basics",
      content:
        "Fill in the client, address and project type first. These details flow straight through to Auto Quote when you finish, so you won\u2019t have to type them again.",
      targetTestId: "input-survey-client-name",
      placement: "bottom",
    },
    {
      id: "quote-survey-3",
      title: "Begin the Walk-Through",
      content:
        "Press Start Survey to add rooms, capture photos, dictate voice notes and list the work items in each space. When you\u2019re done, the report PDF can be sent straight to Auto Quote to build your priced quote.",
      targetTestId: "button-survey-start",
      placement: "top",
    },
  ],
  referrals: [
    {
      id: "referrals-1",
      title: "Monthly Referral Rewards",
      content:
        "When someone subscribes using your referral link, you earn a fixed monthly reward for up to 12 months. The amount depends on their plan: Lite, Starter, or Pro.",
      targetTestId: "card-limited-offer",
      placement: "bottom",
    },
    {
      id: "referrals-2",
      title: "Your Referral Code",
      content:
        "Copy your unique referral code or share the full link directly. Anyone who signs up using it is tracked as your referral automatically.",
      targetTestId: "card-referral-code",
      placement: "bottom",
    },
    {
      id: "referrals-3",
      title: "Withdraw Earnings",
      content:
        "Add your bank account details here, then request a withdrawal whenever your balance reaches the minimum threshold. Payouts are processed directly to your account.",
      targetTestId: "card-withdraw",
      placement: "bottom",
    },
    {
      id: "referrals-4",
      title: "Withdrawal History",
      content:
        "Every payout request is logged here with its status so you can track what has been paid and what is pending.",
      targetTestId: "card-withdrawal-history",
      placement: "bottom",
    },
    {
      id: "referrals-5",
      title: "Have a Referral Code?",
      content:
        "If another builder referred you, enter their code here to activate any discount or reward that applies to your account.",
      targetTestId: "card-apply-referral",
      placement: "bottom",
    },
    {
      id: "referrals-6",
      title: "Your Referrals",
      content:
        "Everyone who has signed up using your link appears here. You can see their plan, how many reward months remain, and the total earnings each referral has generated.",
      targetTestId: "card-referral-list",
      placement: "bottom",
    },
  ],
};

interface TourContextValue {
  isActive: boolean;
  hasSeen: boolean;
  startTour: (steps?: TourStep[]) => void;
  endTour: () => void;
  pageTourIdOverride: string | null;
  setPageTourIdOverride: (id: string | null) => void;
  userId: string | null;
}

const TourContext = createContext<TourContextValue>({
  isActive: false,
  hasSeen: false,
  startTour: () => {},
  endTour: () => {},
  pageTourIdOverride: null,
  setPageTourIdOverride: () => {},
  userId: null,
});

export function useTour() {
  return useContext(TourContext);
}

export function useSetPageTourId(id: string | null) {
  const { setPageTourIdOverride } = useTour();
  useEffect(() => {
    setPageTourIdOverride(id);
    return () => setPageTourIdOverride(null);
  }, [id, setPageTourIdOverride]);
}

export function usePageTour(pageId: keyof typeof PAGE_TOURS) {
  const { startTour, isActive, hasSeen, userId } = useTour();
  const { enabled } = useTutorial();
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (hasStartedRef.current) return;
    if (!enabled) return;
    if (!userId) return; // wait until we know which user this is
    if (!hasSeen) return; // main welcome tour must complete first
    if (!PAGE_TOURS[pageId]?.length) return;
    if (localStorage.getItem(PAGE_KEY(pageId, userId)) === "true") return;
    if (isActive) return;

    hasStartedRef.current = true;

    const timer = setTimeout(() => {
      localStorage.setItem(PAGE_KEY(pageId, userId), "true");
      startTour(PAGE_TOURS[pageId]);
    }, 900);

    return () => clearTimeout(timer);
  }, [enabled, hasSeen, isActive, pageId, startTour, userId]);
}

// Maps the current URL path to a PAGE_TOURS key
function pathToPageId(path: string): keyof typeof PAGE_TOURS | null {
  if (path === "/" || path === "/dashboard") return "dashboard";
  if (path.startsWith("/quotes/new")) return "auto-quote";
  if (path.startsWith("/quotes/template")) return "template-quote";
  if (path.startsWith("/quotes/manual")) return "manual-quote";
  if (path === "/quotes" || path.startsWith("/quotes?")) return "quotes";
  if (path.startsWith("/quotes/survey")) return "quote-survey";
  if (path.startsWith("/site-surveys")) return "site-surveys";
  if (path.startsWith("/clients")) return "clients";
  if (path.startsWith("/cost-library")) return "cost-library";
  if (path.startsWith("/adjustments")) return "adjustments";
  if (path.startsWith("/pricing-intelligence")) return "pricing-intelligence";
  if (path.startsWith("/referrals")) return "referrals";
  if (path.startsWith("/settings")) return "settings";
  return null;
}

interface ElementRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function getRect(testId: string): ElementRect | null {
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

const POPOVER_WIDTH = 370;

function TourOverlay({
  steps,
  currentStep,
  onNext,
  onPrev,
  onEnd,
  onChoose,
}: {
  steps: TourStep[];
  currentStep: number;
  onNext: () => void;
  onPrev: () => void;
  onEnd: () => void;
  onChoose: (pageId: keyof typeof PAGE_TOURS) => void;
}) {
  const step = steps[currentStep];
  const [rect, setRect] = useState<ElementRect | null>(null);
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!step?.targetTestId) {
      setRect(null);
      return;
    }
    let attempts = 0;
    const tryFind = () => {
      const el = document.querySelector(`[data-testid="${step.targetTestId}"]`);
      if (el) {
        // Scroll the element into view first, then measure after the scroll settles
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        retryRef.current = setTimeout(() => {
          const r = getRect(step.targetTestId!);
          if (r) setRect(r);
        }, 400);
        return;
      }
      if (++attempts < 20) {
        retryRef.current = setTimeout(tryFind, 120);
      }
    };
    tryFind();
    const onReposition = () => {
      if (step.targetTestId) {
        const r = getRect(step.targetTestId);
        if (r) setRect(r);
      }
    };
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
      if (retryRef.current) clearTimeout(retryRef.current);
    };
  }, [step?.targetTestId, currentStep]);

  if (!step) return null;

  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;
  const padding = step.highlightPadding ?? 10;

  const handleSkip = () => {
    onEnd();
    toast({
      title: "Tour skipped",
      description:
        "You can restart the tour anytime by clicking the graduation cap icon in the top navigation bar.",
      duration: 5000,
    });
  };

  const spotlightStyle: React.CSSProperties = rect
    ? {
        position: "fixed",
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        boxShadow: "0 0 0 9999px rgba(0,0,0,0.62)",
        borderRadius: 12,
        zIndex: 9999,
        pointerEvents: "none",
        transition:
          "top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease",
        outline: "2px solid hsl(var(--primary))",
        outlineOffset: 0,
      }
    : {};

  let popoverStyle: React.CSSProperties = {};

  if (!rect || step.placement === "center") {
    popoverStyle = {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 10001,
      width: POPOVER_WIDTH,
    };
  } else if (step.placement === "right") {
    popoverStyle = {
      position: "fixed",
      left: Math.min(
        rect.left + rect.width + padding + 16,
        window.innerWidth - POPOVER_WIDTH - 16
      ),
      top: Math.max(16, rect.top + rect.height / 2 - 140),
      zIndex: 10001,
      width: POPOVER_WIDTH,
    };
  } else if (step.placement === "left") {
    popoverStyle = {
      position: "fixed",
      right: window.innerWidth - rect.left + padding + 16,
      top: Math.max(16, rect.top + rect.height / 2 - 140),
      zIndex: 10001,
      width: POPOVER_WIDTH,
    };
  } else if (step.placement === "bottom") {
    popoverStyle = {
      position: "fixed",
      top: Math.min(
        rect.top + rect.height + padding + 16,
        window.innerHeight - 280
      ),
      left: Math.max(
        16,
        Math.min(
          rect.left + rect.width / 2 - POPOVER_WIDTH / 2,
          window.innerWidth - POPOVER_WIDTH - 16
        )
      ),
      zIndex: 10001,
      width: POPOVER_WIDTH,
    };
  } else if (step.placement === "top") {
    popoverStyle = {
      position: "fixed",
      bottom: Math.max(16, window.innerHeight - rect.top + padding + 16),
      left: Math.max(
        16,
        Math.min(
          rect.left + rect.width / 2 - POPOVER_WIDTH / 2,
          window.innerWidth - POPOVER_WIDTH - 16
        )
      ),
      zIndex: 10001,
      width: POPOVER_WIDTH,
    };
  }

  return createPortal(
    <>
      {!rect && (
        <div
          className="fixed inset-0 bg-black/62 z-[9998]"
          data-testid="tour-overlay-bg"
        />
      )}
      {rect && (
        <>
          <div
            style={spotlightStyle}
            data-testid="tour-spotlight"
            aria-hidden
          />
          <div
            className="fixed inset-0 z-[9998]"
            onClick={handleSkip}
            data-testid="tour-overlay-click"
          />
        </>
      )}

      <div
        style={popoverStyle}
        className="bg-card border border-border rounded-2xl shadow-2xl p-5 animate-in fade-in zoom-in-95 duration-200"
        data-testid={`tour-step-${step.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-bold text-base text-foreground leading-snug">
            {step.title}
          </h3>
          <button
            onClick={handleSkip}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0 mt-0.5"
            data-testid="button-tour-close"
            aria-label="Close tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="text-sm text-muted-foreground leading-relaxed mb-3">
          {step.content}
        </div>

        {step.isChoiceStep && (
          <div className="grid gap-2 mb-4 mt-1">
            <Link
              href="/quotes/new"
              onClick={() => onChoose("auto-quote")}
              className="flex items-center gap-3 rounded-xl border border-border bg-muted/50 hover:bg-primary/10 hover:border-primary/40 transition-colors px-4 py-3 text-sm font-medium"
              data-testid="tour-choose-auto"
            >
              <Zap className="w-4 h-4 text-primary shrink-0" />
              <div>
                <p className="font-semibold text-foreground">Auto Quote</p>
                <p className="text-xs text-muted-foreground font-normal">
                  Smart quoting (recommended)
                </p>
              </div>
            </Link>
            <Link
              href="/quotes/template"
              onClick={() => onChoose("template-quote")}
              className="flex items-center gap-3 rounded-xl border border-border bg-muted/50 hover:bg-primary/10 hover:border-primary/40 transition-colors px-4 py-3 text-sm font-medium"
              data-testid="tour-choose-template"
            >
              <ListChecks className="w-4 h-4 text-primary shrink-0" />
              <div>
                <p className="font-semibold text-foreground">Template Quote</p>
                <p className="text-xs text-muted-foreground font-normal">
                  Start from a pre-set structure
                </p>
              </div>
            </Link>
            <Link
              href="/quotes/manual"
              onClick={() => onChoose("manual-quote")}
              className="flex items-center gap-3 rounded-xl border border-border bg-muted/50 hover:bg-primary/10 hover:border-primary/40 transition-colors px-4 py-3 text-sm font-medium"
              data-testid="tour-choose-manual"
            >
              <PenLine className="w-4 h-4 text-primary shrink-0" />
              <div>
                <p className="font-semibold text-foreground">Manual Quote</p>
                <p className="text-xs text-muted-foreground font-normal">
                  Full control, line by line
                </p>
              </div>
            </Link>
          </div>
        )}

        <div className="flex items-center gap-1 mb-4">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === currentStep
                  ? "w-6 bg-primary"
                  : i < currentStep
                  ? "w-1.5 bg-primary/40"
                  : "w-1.5 bg-muted-foreground/20"
              }`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3">
          {!step.isChoiceStep ? (
            <button
              onClick={handleSkip}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
              data-testid="button-tour-skip"
            >
              Skip tour
            </button>
          ) : (
            <button
              onClick={onEnd}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
              data-testid="button-tour-skip"
            >
              Skip for now
            </button>
          )}
          <div className="flex gap-2">
            {!isFirst && (
              <Button
                variant="outline"
                size="sm"
                onClick={onPrev}
                className="h-8 px-3 text-xs"
                data-testid="button-tour-prev"
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                Back
              </Button>
            )}
            {!step.isChoiceStep && (
              <Button
                size="sm"
                onClick={isLast ? onEnd : onNext}
                className="h-8 px-4 text-xs"
                data-testid="button-tour-next"
              >
                {isLast ? (
                  "Done!"
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { enabled } = useTutorial();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  // Start as "seen" until we know the user — prevents premature firing while auth loads
  const [hasSeen, setHasSeen] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [steps, setSteps] = useState<TourStep[]>(MAIN_TOUR_STEPS);
  const [currentStep, setCurrentStep] = useState(0);
  const [pageTourIdOverride, setPageTourIdOverride] = useState<string | null>(null);

  // Once userId is known, read the correct per-user seen key
  useEffect(() => {
    if (!userId) return;
    setHasSeen(localStorage.getItem(MAIN_SEEN_KEY(userId)) === "true");
  }, [userId]);

  const startTour = useCallback((customSteps?: TourStep[]) => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setSteps(customSteps ?? MAIN_TOUR_STEPS);
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  const endTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    if (userId) localStorage.setItem(MAIN_SEEN_KEY(userId), "true");
    setHasSeen(true);
  }, [userId]);

  // Called when a user picks a quote type on the choose step — seamlessly
  // transitions into that page's tutorial without ever ending the tour.
  const choosePageTour = useCallback((pageId: keyof typeof PAGE_TOURS) => {
    if (userId) {
      localStorage.setItem(MAIN_SEEN_KEY(userId), "true");
      localStorage.setItem(PAGE_KEY(pageId, userId), "true");
    }
    setHasSeen(true);
    // Auto Quote now has its own on-demand mascot, not an automatic overlay.
    if (pageId === "auto-quote") {
      setIsActive(false);
      setCurrentStep(0);
      return;
    }
    setSteps(PAGE_TOURS[pageId]);
    setCurrentStep(0);
  }, [userId]);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
  }, [steps.length]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, []);

  // The main Welcome tour belongs on the Dashboard. New users now complete the
  // Test Quote Introduction first, so opening a generated quote (or any other
  // page) must not trigger the tour before they deliberately visit Dashboard.
  useEffect(() => {
    const isDashboard = location === "/"
      || location === "/dashboard"
      || location.startsWith("/dashboard?");
    if (!hasSeen && enabled && userId && isDashboard) {
      const timer = setTimeout(() => startTour(), 1500);
      return () => clearTimeout(timer);
    }
    return;
  }, [hasSeen, enabled, location, startTour, userId]);

  return (
    <TourContext.Provider value={{ isActive, hasSeen, startTour, endTour, pageTourIdOverride, setPageTourIdOverride, userId }}>
      {children}
      {isActive && (
        <TourOverlay
          steps={steps}
          currentStep={currentStep}
          onNext={nextStep}
          onPrev={prevStep}
          onEnd={endTour}
          onChoose={choosePageTour}
        />
      )}
    </TourContext.Provider>
  );
}

export function TourButton() {
  const { startTour, isActive, pageTourIdOverride } = useTour();
  const { enabled } = useTutorial();
  const [location] = useLocation();

  const quoteGuide = location === "/quotes/new" || location.startsWith("/quotes/new?");
  if (!enabled && !quoteGuide) return null;

  const handleClick = () => {
    if (quoteGuide) {
      window.dispatchEvent(new Event("tpt:quote-guide-open"));
      return;
    }
    const pageId = pageTourIdOverride ?? pathToPageId(location);
    if (pageId && PAGE_TOURS[pageId as keyof typeof PAGE_TOURS]) {
      startTour(PAGE_TOURS[pageId as keyof typeof PAGE_TOURS]);
    } else {
      startTour();
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleClick}
      data-testid="button-open-tour"
      title={quoteGuide ? "Quote guide" : "Guided tour"}
      aria-label={quoteGuide ? "Open quote guide" : "Open guided tour"}
      className={`relative ${isActive ? "text-[#5aabbf]" : ""}`}
    >
      <GraduationCap className={`h-5 w-5 transition-colors ${isActive ? "text-[#5aabbf]" : ""}`} />
    </Button>
  );
}
